import { z } from 'zod'
import {
  isEncounterClosed,
  isEncounterResolved,
  StrictSet,
} from '@joyful/utils'
import type { components } from '@joyful/openapi/types.gen'
import type { HasCapability } from '../../../../hooks/useHasUserCapability'
import type { ActionMetadatum, ActionSubpath } from './encounterActionTypes'

type ActionMetadatumBase = Omit<ActionMetadatum, 'formComponent'>

export const EncounterActionMetadataBase: Record<
  ActionSubpath,
  ActionMetadatumBase
> = {
  'check-eligibility': {
    capability: 'encounter.eligibility_check.run',
    description: "Check patient's insurance eligibility",
    emoji: '🗂️',
    title: 'Check eligibility',
  },
  close: {
    capability: 'encounter.action.close',
    description: 'QA / close the encounter',
    emoji: '🎯',
    enabledRequirements: new StrictSet(['resolved']),
    title: 'Close encounter',
  },
  'draft-appeal': {
    capability: 'outbound_packets.draft',
    description: 'Generate an appeal letter',
    emoji: '✏️',
    enabledRequirements: new StrictSet(['flagged-for-follow-up']),
    title: 'Assemble appeal',
  },
  escalate: {
    capability: [
      'encounter.action.escalate.joyful',
      'encounter.action.escalate.supervisor',
    ],
    description:
      'Get help with this claim - escalate to Joyful or your supervisor.',
    emoji: '⚠️',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-closed',
    ]),
    title: 'Escalate',
  },
  'payer-action': {
    capability: [
      'encounter.action.record.denial',
      'encounter.action.record.payment',
      'encounter.action.record.recoupment',
      'encounter.action.record.rejection',
    ],
    description: 'I discovered payer actions outside the platform.',
    emoji: '💰',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-closed',
    ]),
    title: 'Manually found payer action',
  },
  'payer-action:found-denial': {
    capability: 'encounter.action.record.denial',
    description:
      'I discovered a denial (via payer portal, fax, or phone) that needs to be logged.',
    emoji: '❌',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-closed',
    ]),
    title: 'Found denial',
  },
  'payer-action:found-payment': {
    capability: 'encounter.action.record.payment',
    description:
      'I discovered a payment (via payer portal, fax, or phone) that needs to be posted.',
    emoji: '💰',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-closed',
    ]),
    title: 'Found payment',
  },
  'payer-action:found-recoupment': {
    capability: 'encounter.action.record.recoupment',
    description:
      'I discovered a recoupment (payer is taking money back) that needs to be logged.',
    emoji: '⤴️',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-closed',
    ]),
    title: 'Found recoupment',
  },
  'payer-action:found-rejection': {
    capability: 'encounter.action.record.rejection',
    description:
      'I discovered a rejection (via clearinghouse, payer portal, or fax) that needs to be logged.',
    emoji: '↩️',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-closed',
    ]),
    title: 'Found rejection',
  },
  'place-call': {
    capability: 'phone.call.place',
    description: 'Place a phone call to the payer, practice, etc.',
    emoji: '📞',
    enabledRequirements: new StrictSet(['flagged-for-follow-up']),
    title: 'Place phone call',
  },
  'qa-review': {
    capability: 'encounter_audit.review',
    description: 'Review a closed encounter for accuracy',
    emoji: '📋',
    title: 'Audit closed encounter',
  },
  research: {
    capability: 'encounter.action.record.research',
    description: 'I researched / investigated.',
    emoji: '🔍',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-resolved',
      'not-closed',
    ]),
    title: 'Research',
  },
  resolve: {
    capability: 'encounter.action.resolve',
    description:
      'Payment found/received and ready to close, or claim uncollectible.',
    emoji: '✅',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-resolved',
      'not-closed',
    ]),
    title: 'Resolve claim',
  },
  'resolve:found-money': {
    capability: 'encounter.action.record.payment',
    columnSpan: 'wide',
    emoji: '✅',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-closed',
    ]),
    title: 'A JH biller recovered / found money for this claim',
  },
  'resolve:uncollectible': {
    capability: 'encounter.action.resolve',
    emoji: '✖️',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-closed',
    ]),
    title: 'Claim is uncollectible',
  },
  'return-to-biller': {
    capability: 'encounter.workflow_status.manage.return_to_biller',
    description:
      'Return this escalated claim to the biller for further action.',
    emoji: '↩️',
    enabledRequirements: new StrictSet([
      'flagged-for-follow-up',
      'not-in-queue',
      'not-in-progress-return-to-biller',
      'not-closed',
    ]),
    title: 'Return claim to biller',
  },
  snooze: {
    capability: 'encounter.action.snooze',
    description: 'Temporarily pause work',
    emoji: '⏰',
    enabledRequirements: new StrictSet([
      'flagged-for-follow-up',
      'not-rejected',
      'not-resolved',
      'not-closed',
      'not-snoozed',
    ]),
    title: 'Snooze',
  },
  'submit-paper-claim': {
    capability: 'outbound_packets.draft',
    description: 'Mail a paper claim',
    emoji: '📬',
    enabledRequirements: new StrictSet(['flagged-for-follow-up']),
    title: 'Submit paper claim',
  },
  submitted: {
    capability: 'encounter.action.record.documentation',
    description:
      'I submitted something to payer (appeal, documentation, claim).',
    emoji: '🎯',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-resolved',
      'not-closed',
    ]),
    title: 'Submitted',
  },
  'submitted:claim-resubmission': {
    capability: 'encounter.action.record.claim_resubmission',
    description: 'I submitted a claim resubmission or correction',
    emoji: '🔄',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-resolved',
      'not-closed',
    ]),
    title: 'Claim resubmission / correction',
  },
  'submitted:confirmed-with-payer-batch-processing': {
    capability: 'encounter.action.confirm_payment',
    description: 'Submitted to payer for batch processing',
    emoji: '⏳',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-resolved',
      'not-closed',
    ]),
    title: 'Submitted to payer for batch processing',
  },
  'submitted:submit-appeal': {
    capability: 'encounter.action.record.appeal',
    description: 'I submitted an appeal to the payer',
    emoji: '⚖️',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-resolved',
      'not-closed',
    ]),
    title: 'Submit appeal',
  },
  'submitted:submit-documentation-details': {
    capability: 'encounter.action.record.documentation',
    description: 'I submitted documentation details',
    emoji: '📄',
    enabledRequirements: new StrictSet([
      'assigned-to-current-user',
      'flagged-for-follow-up',
      'not-resolved',
      'not-closed',
    ]),
    title: 'Submit documentation details',
  },
}

export const ActionSubpathSchema = z.enum(
  // SAFETY: EncounterActionMetadataBase is keyed by ActionSubpath, so Object.keys is that tuple.
  Object.keys(EncounterActionMetadataBase) as [
    ActionSubpath,
    ...Array<ActionSubpath>,
  ],
)

interface ActionGroup {
  actions: Array<ActionSubpath>
  title: string
}

export function buildActionGroups({
  encounter,
}: {
  encounter: components['schemas']['EncounterAPIResponse']
}) {
  const isResolved = isEncounterResolved(encounter)
  const isClosed = isEncounterClosed(encounter)
  const canClose = isResolved && !isClosed

  return (
    [
      canClose
        ? {
            actions: ['close', 'qa-review'] as const,
            title: 'Admin',
          }
        : null,
      {
        actions: ['research', 'submitted', 'payer-action'] as const,
        title: "Log what you've done",
      },
      {
        actions: [
          'snooze',
          'escalate',
          'return-to-biller',
          'resolve',
          'check-eligibility',
          'place-call',
          'draft-appeal',
          'submit-paper-claim',
        ] as const,
        title: 'Take action now',
      },
      canClose
        ? null
        : {
            actions: ['close', 'qa-review'] as const,
            title: 'Admin',
          },
    ] satisfies Array<null | ActionGroup>
  ).filter(Boolean)
}

export function isEncounterActionAvailableToUser({
  action,
  hasCapability,
}: {
  action: ActionSubpath
  hasCapability: HasCapability
}) {
  const metadata = EncounterActionMetadataBase[action]
  if (metadata.capability === undefined) {
    return true
  }

  return Array.isArray(metadata.capability)
    ? metadata.capability.some((capability) => {
        return hasCapability(capability)
      })
    : hasCapability(metadata.capability)
}
